import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';

import { FuseConfigService } from '@fuse/services/config.service';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';

import { EventEmitterService } from '../../../main/profile/services/event-emitter.service';

import { navigation } from 'app/navigation/navigation';

import { Router } from '@angular/router';

import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { locale as english } from './i18n/en';
import { locale as french } from './i18n/fr';
import { locale as spanish } from './i18n/es';

import { UserPhotoService } from '../../../core/ProfileService/user-photo.service';
import { AuthentificationService } from '../../../core/AuthService/authentification.service';
import { ModifyProfileInformationData } from 'app/main/profile/interfaces/ModifyProfileInformationData';

@Component({
    selector: 'toolbar',
    templateUrl: './toolbar.component.html',
    styleUrls: ['./toolbar.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class ToolbarComponent implements OnInit, OnDestroy {
    horizontalNavbar: boolean;
    rightNavbar: boolean;
    hiddenNavbar: boolean;
    languages: any;
    navigation: any;
    selectedLanguage: any;
    userStatusOptions: any[];

    userPhotoUrl: string;
    userName: string;

    // Private
    private _unsubscribeAll: Subject<any>;

    /**
     * Constructor
     *
     * @param {FuseConfigService} _fuseConfigService
     * @param {FuseSidebarService} _fuseSidebarService
     * @param {TranslateService} _translateService
     * @param {FuseTranslationLoaderService} _fuseTranslationLoaderService
     */
    constructor(
        private _fuseConfigService: FuseConfigService,
        private _fuseSidebarService: FuseSidebarService,
        private _translateService: TranslateService,
        private _fuseTranslationLoaderService: FuseTranslationLoaderService,
        private router: Router,
        private userPhotoService: UserPhotoService,
        private authentificationService: AuthentificationService,
        private eventEmitterService: EventEmitterService
    ) {
        this._fuseTranslationLoaderService.loadTranslations(english, french, spanish);

        // Set the defaults
        this.userStatusOptions = [
            {
                title: 'Online',
                icon: 'icon-checkbox-marked-circle',
                color: '#4CAF50'
            },
            {
                title: 'Away',
                icon: 'icon-clock',
                color: '#FFC107'
            },
            {
                title: 'Do not Disturb',
                icon: 'icon-minus-circle',
                color: '#F44336'
            },
            {
                title: 'Invisible',
                icon: 'icon-checkbox-blank-circle-outline',
                color: '#BDBDBD'
            },
            {
                title: 'Offline',
                icon: 'icon-checkbox-blank-circle-outline',
                color: '#616161'
            }
        ];

        this.languages = [
            {
                id: 'en',
                title: 'English',
                flag: 'uk'
            },
            {
                id: 'fr',
                title: 'Français',
                flag: 'fr'
            },
            {
                id: 'es',
                title: 'Español',
                flag: 'es'
            }
        ];

        this.navigation = navigation;

        // Set the private defaults
        this._unsubscribeAll = new Subject();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to the config changes
        this._fuseConfigService.config
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((settings) => {
                this.horizontalNavbar = settings.layout.navbar.position === 'top';
                this.rightNavbar = settings.layout.navbar.position === 'right';
                this.hiddenNavbar = settings.layout.navbar.hidden === true;
            });

        // Set the selected language from default languages
        this.selectedLanguage = _.find(this.languages, { id: this._translateService.currentLang });

        // Subscribe to profile information changes
        this.eventEmitterService.subsVarGetProfileInformation = this.eventEmitterService
            .invokeGetProfileInformation.subscribe((newProfileInformation: ModifyProfileInformationData) => {
                this.updateProfileInformation(newProfileInformation);
            });

        this.eventEmitterService.subsVarGetProfilePhoto = this.eventEmitterService
            .invokeGetProfilePhoto.subscribe((newProfilePhoto: string) => {
                this.updateProfilePhoto(newProfilePhoto);
            });

        // Set photo and username
        if (localStorage.getItem('isAuthenticated') == 'true') {
            if (localStorage.getItem('hasPhotoChanged') == 'true') {
                this.userPhotoUrl = localStorage.getItem('photo');
            } else {
                this.userPhotoUrl = this.userPhotoService.getUserPhotoURL();
            }
            this.userName = localStorage.getItem('first_name') + ' ' + localStorage.getItem('last_name');
        } else {
            this.userPhotoUrl = 'assets/images/avatars/profile.jpg';
            this.userName = '';
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
        this.eventEmitterService.subsVarGetProfileInformation.unsubscribe();
        this.eventEmitterService.subsVarGetProfilePhoto.unsubscribe();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebarOpen(key): void {
        this._fuseSidebarService.getSidebar(key).toggleOpen();
    }

    /**
     * Search
     *
     * @param value
     */
    search(value): void {
        // Do your search here...
        console.log(value);
    }

    /**
     * Set the language
     *
     * @param lang
     */
    setLanguage(lang): void {
        // Set the selected language for the toolbar
        this.selectedLanguage = lang;

        // Use the selected language for translations
        this._translateService.use(lang.id);
    }

    /**
     * Logout
     */
    logout() {
        this.authentificationService.logout();
    }

    /**
     * navigateToProfile
     */
    navigateToProfile() {
        this.router.navigate(['profile']);
    }

    updateProfileInformation(newProfileInformation: ModifyProfileInformationData) {
        this.userName = newProfileInformation.firstname + ' ' + newProfileInformation.lastname;
    }

    updateProfilePhoto(newProfilePhoto: string) {
        this.userPhotoUrl = newProfilePhoto;
        localStorage.setItem('hasPhotoChanged', 'true');
    }
}
